/**
 * Boundary helpers that turn a raw `Response` into a typed `Result`. Every JSON
 * the edge emits is the shared discriminated envelope (`{ ok, data } | { ok,
 * error }`); these unwrap it into `Result<T, ApiError>` so callers branch on the
 * value, never on HTTP status codes scattered through the UI. A body that is not
 * a valid envelope, or whose `data` fails its schema, becomes a
 * {@link MalformedResponseError} (surfaced as an `ApiError`), so contract drift
 * fails loud instead of rendering `undefined`.
 */
import { type ApiError, type AppError, apiResponse, err, ok, type Result } from '@4f/shared';
import type { ZodType } from 'zod';
import { MalformedResponseError, NetworkError } from './errors.ts';

/** Flatten an `AppError` (transport failure) into the wire `ApiError` shape the UI reads. */
export function toApiError(error: AppError): ApiError {
  return { code: error.code, message: error.message };
}

/**
 * Read `response` as JSON and validate it against the shared envelope wrapping
 * `dataSchema`. Success → `ok(data)`; a well-formed error envelope (any status)
 * → `err(error)` verbatim (so `402`/`403`/`404` domain outcomes flow through);
 * anything else → `err(malformed)`. The HTTP status is intentionally NOT trusted
 * on its own — the envelope's `ok` flag is the contract.
 */
export async function readEnvelope<T>(
  response: Response,
  dataSchema: ZodType<T>,
): Promise<Result<T, ApiError>> {
  let raw: unknown;
  try {
    raw = await response.json();
  } catch {
    return err(toApiError(new MalformedResponseError(`invalid JSON (HTTP ${response.status})`)));
  }

  const parsed = apiResponse(dataSchema).safeParse(raw);
  if (!parsed.success) {
    return err(
      toApiError(new MalformedResponseError(parsed.error.issues[0]?.message ?? 'bad shape')),
    );
  }

  // Zod's discriminated-union output does not narrow through a generic `T`, so
  // re-assert the concrete envelope shape here (validation already guaranteed it).
  const envelope = parsed.data as { ok: true; data: T } | { ok: false; error: ApiError };
  return envelope.ok ? ok(envelope.data) : err(envelope.error);
}

/** Wrap a `fetch` in a `Result` so a network throw becomes a typed `err`, never an exception. */
export async function safeFetch(
  doFetch: () => Promise<Response>,
): Promise<Result<Response, ApiError>> {
  try {
    return ok(await doFetch());
  } catch (cause) {
    return err(toApiError(new NetworkError(cause)));
  }
}
